import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { form, FormField } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DividerComponent } from '@shared/components/divider.component';
import { QuillViewComponent } from 'ngx-quill';
import { Observable } from 'rxjs';
import { FAST_TRACK_DIALOG_TEXTS, FAST_TRACK_TERMINAL_STATUSES } from '../fast-track.constants';
import { FastTrackDetail, FastTrackDialogResult } from '../fast-track.model';
import { FastTrackApiService } from '../services/fast-track-api.service';

@Component({
  selector: 'app-fast-track-confirmation-dialog',
  imports: [MatDialogModule, MatButtonModule, MatCheckboxModule, ReactiveFormsModule, QuillViewComponent, DividerComponent, FormField],
  templateUrl: './fast-track-confirmation-dialog.component.html',
  styleUrl: './fast-track-confirmation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FastTrackConfirmationDialogComponent {
  private readonly fastTrackApiService = inject(FastTrackApiService);

  readonly texts = FAST_TRACK_DIALOG_TEXTS;
  readonly data = inject<FastTrackDetail>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<FastTrackConfirmationDialogComponent, FastTrackDialogResult>);

  readonly confirmationModel = signal({ confirmed: false });
  readonly confirmationForm = form(this.confirmationModel);

  readonly submitting = signal(false);
  readonly canContinueWithConfirm = computed(() => this.confirmationModel().confirmed && !this.submitting());

  confirm(): void {
    if (!this.confirmationModel().confirmed) {
      return;
    }

    this.submit(() => this.fastTrackApiService.confirmFastTrack$(this.data.assessmentId), 'confirmed');
  }

  decline(): void {
    this.submit(() => this.fastTrackApiService.declineFastTrack$(this.data.assessmentId), 'declined');
  }

  private submit(resolveFastTrackRequest: () => Observable<void>, result: FastTrackDialogResult): void {
    if (this.submitting()) {
      return;
    }

    this.submitting.set(true);
    resolveFastTrackRequest().subscribe({
      next: () => this.dialogRef.close(result),
      error: (error: HttpErrorResponse) => {
        if (FAST_TRACK_TERMINAL_STATUSES.includes(error.status)) {
          this.dialogRef.close('unavailable');
        } else {
          this.submitting.set(false);
        }
      },
    });
  }
}
